import React, { useEffect, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';

const ringExpand = keyframes`
    0%   { transform: translate(-50%, -50%) scale(0.8); opacity: 0.6; }
    100% { transform: translate(-50%, -50%) scale(1);   opacity: 1;   }
`;

const CursorDot = styled.div`
    position: fixed;
    width: 8px;
    height: 8px;
    background: #667eea;
    border-radius: 50%;
    pointer-events: none;
    z-index: 99999;
    transform: translate(-50%, -50%);
    transition: width 0.15s ease, height 0.15s ease, background 0.15s ease;
    will-change: left, top;

    &.hovering {
        width: 12px;
        height: 12px;
        background: #f093fb;
    }

    &.clicking {
        width: 6px;
        height: 6px;
        background: #06b6d4;
    }
`;

const CursorRing = styled.div`
    position: fixed;
    width: 38px;
    height: 38px;
    border: 2px solid rgba(102, 126, 234, 0.65);
    border-radius: 50%;
    pointer-events: none;
    z-index: 99998;
    transform: translate(-50%, -50%);
    transition:
        width 0.25s ease,
        height 0.25s ease,
        border-color 0.25s ease,
        transform 0.08s ease;
    will-change: left, top;
    animation: ${ringExpand} 0.3s ease forwards;

    &.hovering {
        width: 54px;
        height: 54px;
        border-color: rgba(240, 147, 251, 0.8);
    }

    &.clicking {
        transform: translate(-50%, -50%) scale(0.75);
        border-color: rgba(6, 182, 212, 0.9);
    }
`;

const isTouchDevice = () =>
    typeof window !== 'undefined' &&
    window.matchMedia('(pointer: coarse)').matches;

const CustomCursor = () => {
    const [dotPos, setDotPos]   = useState({ x: -200, y: -200 });
    const [ringPos, setRingPos] = useState({ x: -200, y: -200 });
    const [hovering, setHovering] = useState(false);
    const [clicking, setClicking] = useState(false);

    const mouseRef = useRef({ x: -200, y: -200 });
    const ringRef  = useRef({ x: -200, y: -200 });
    const rafRef   = useRef(null);

    useEffect(() => {
        if (isTouchDevice()) return;

        const onMouseMove = (e) => {
            mouseRef.current = { x: e.clientX, y: e.clientY };
            setDotPos({ x: e.clientX, y: e.clientY });
        };

        const onMouseDown = () => setClicking(true);
        const onMouseUp   = () => setClicking(false);

        const onMouseOver = (e) => {
            const tag    = e.target.tagName?.toLowerCase();
            const cursor = window.getComputedStyle(e.target).cursor;
            if (tag === 'a' || tag === 'button' || cursor === 'pointer') {
                setHovering(true);
            }
        };

        const onMouseOut = () => setHovering(false);

        const animateRing = () => {
            const { x: mx, y: my } = mouseRef.current;
            const { x: rx, y: ry } = ringRef.current;
            const nx = rx + (mx - rx) * 0.13;
            const ny = ry + (my - ry) * 0.13;
            ringRef.current = { x: nx, y: ny };
            setRingPos({ x: nx, y: ny });
            rafRef.current = requestAnimationFrame(animateRing);
        };

        window.addEventListener('mousemove',  onMouseMove);
        window.addEventListener('mousedown',  onMouseDown);
        window.addEventListener('mouseup',    onMouseUp);
        document.addEventListener('mouseover', onMouseOver);
        document.addEventListener('mouseout',  onMouseOut);
        rafRef.current = requestAnimationFrame(animateRing);

        return () => {
            window.removeEventListener('mousemove',  onMouseMove);
            window.removeEventListener('mousedown',  onMouseDown);
            window.removeEventListener('mouseup',    onMouseUp);
            document.removeEventListener('mouseover', onMouseOver);
            document.removeEventListener('mouseout',  onMouseOut);
            cancelAnimationFrame(rafRef.current);
        };
    }, []);

    if (isTouchDevice()) return null;

    const ringClass = `${hovering ? 'hovering' : ''} ${clicking ? 'clicking' : ''}`.trim();
    const dotClass  = `${hovering ? 'hovering' : ''} ${clicking ? 'clicking' : ''}`.trim();

    return (
        <>
            <CursorDot
                className={dotClass}
                style={{ left: dotPos.x, top: dotPos.y }}
            />
            <CursorRing
                className={ringClass}
                style={{ left: ringPos.x, top: ringPos.y }}
            />
        </>
    );
};

export default CustomCursor;
